import { useCallback, useState } from 'react';
import { getHolidayCalendars, type HolidayCalendar } from '../api/holiday.api';

/** Holiday with user assignment info, for rendering on the scheduler per-user. */
export interface UserHoliday {
  date: string; // ISO date YYYY-MM-DD
  name: string;
  /** Monday user IDs this holiday applies to */
  assignedUserIds: string[];
}

/**
 * Hook that fetches holidays from all holiday calendars and returns:
 * - `holidays`: Map<string, string> keyed by ISO date → holiday name (all calendars)
 * - `userHolidays`: UserHoliday[] with per-user assignment info for scheduler events
 *
 * For default calendars (is_default=true), holidays apply to ALL visible users
 * passed via `allUserIds`. For non-default calendars, holidays apply only to
 * explicitly assigned users.
 *
 * Usage:
 *   const { holidays, userHolidays, fetchHolidays } = useHolidays();
 *   useEffect(() => { fetchHolidays(startDate, endDate, allUserIds); }, [startDate, endDate, allUserIds]);
 */
export function useHolidays() {
  const [holidays, setHolidays] = useState<Map<string, string>>(new Map());
  const [userHolidays, setUserHolidays] = useState<UserHoliday[]>([]);
  /** All holidays (not filtered by date range) — used by workload computation
   *  so that items spanning beyond the visible range get correct hour distribution. */
  const [allUserHolidays, setAllUserHolidays] = useState<UserHoliday[]>([]);

  const fetchHolidays = useCallback(async (
    startDate: string,
    endDate: string,
    allUserIds?: string[],
  ) => {
    try {
      const calendars = await getHolidayCalendars();
      const map = new Map<string, string>();
      const userHolidayList: UserHoliday[] = [];
      const allUserHolidayList: UserHoliday[] = [];

      for (const cal of calendars) {
        // Default calendars apply to ALL users; non-default only to assigned users
        const effectiveUserIds = cal.is_default
          ? (allUserIds ?? [])
          : cal.assignedUserIds;

        for (const holiday of cal.holidays) {
          // Normalize date — backend may return "2026-01-01" or "2026-01-01T00:00:00.000Z"
          const dateStr = String(holiday.date).slice(0, 10);

          // Unfiltered list for workload computation (covers full item timelines)
          if (effectiveUserIds.length > 0) {
            allUserHolidayList.push({
              date: dateStr,
              name: holiday.name,
              assignedUserIds: effectiveUserIds,
            });
          }

          // Only include holidays within the requested date range for rendering
          if (dateStr >= startDate && dateStr <= endDate) {
            map.set(dateStr, holiday.name);

            // Track user assignments for scheduler rendering
            if (effectiveUserIds.length > 0) {
              userHolidayList.push({
                date: dateStr,
                name: holiday.name,
                assignedUserIds: effectiveUserIds,
              });
            }
          }
        }
      }

      setHolidays(map);
      setUserHolidays(userHolidayList);
      setAllUserHolidays(allUserHolidayList);
    } catch {
      setHolidays(new Map());
      setUserHolidays([]);
      setAllUserHolidays([]);
    }
  }, []);

  return { holidays, userHolidays, allUserHolidays, fetchHolidays };
}
